import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/core/styles';
import axios from 'axios';
import { API_URL } from 'config.jsx';
import HealthGraphPlot from './HealthGraphPlot';
import MeasureSelection from './MeasureSelection';
import MeasureConverter from './MeasureConverter';

const styles = {
  centered: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    flex: 1,
  },
  fullHeight: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
};

export class HealthGraph extends Component {
  constructor(props) {
    super(props);

    this.state = {
      plotData: [
        {
          x: [],
          y: [],
          type: 'scatter',
          mode: 'lines+markers',
          marker: { color: 'red' },
        },
      ],
      plotLayout: {
        autosize: true,
        paper_bgcolor: 'rgba(0,0,0,0)',
        plot_bgcolor: 'rgba(0,0,0,0.05)',
        showlegend: false,
      },
    };

    this.getMeasure = this.getMeasure.bind(this);
    this.getMeasures = this.getMeasures.bind(this);
    this.getPlotDataObjects = this.getPlotDataObjects.bind(this);
    this.getPlotLayoutObject = this.getPlotLayoutObject.bind(this);
    this.getMeasuresWithBloodPressure = this.getMeasuresWithBloodPressure.bind(this);
    this.getBloodCellMeasuresAndConvertToPlotObjects = this
      .getBloodCellMeasuresAndConvertToPlotObjects.bind(this);
    this.getMeasuresForCorrelationPlot = this.getMeasuresForCorrelationPlot.bind(this);
    this.resetZoom = this.resetZoom.bind(this);
  }

  getMeasure(measureCodePk) {
    const { elephantId } = this.props;
    return axios.get(
      `${API_URL}elephants/${elephantId}/measurecodes/${measureCodePk}/measures/`,
    );
  }

  getMeasures(measureCodes) {
    Promise.all(measureCodes
      .map(measureCode => this.getMeasure(measureCode.pk)))
      .then(([acct, perms]) => {
        const measureObjects = [acct, perms].filter(response => response !== undefined)
          .map(response => response.data);
        const plotData = this.getPlotDataObjects(measureObjects, 1);
        const title = MeasureConverter.createPlotTitleForWarnings(measureObjects, measureCodes);
        const plotLayout = this.getPlotLayoutObject([measureCodes, 1], title);
        this.setState({ plotData, plotLayout });
      });
  }

  getMeasuresWithBloodPressure(measureCodesWithNewAxisIndex) {
    const measureCodes = measureCodesWithNewAxisIndex[0];
    const newAxisIndex = measureCodesWithNewAxisIndex[1];
    Promise.all(measureCodes
      .map(measureCode => this.getMeasure(measureCode.pk)))
      .then(([sistMeasures, diastMeasures, otherMeasures]) => {
        const measureObjects = [sistMeasures, diastMeasures, otherMeasures]
          .filter(response => response !== undefined)
          .map(response => response.data);
        const plotData = this.getPlotDataObjects(measureObjects, newAxisIndex);
        const title = MeasureConverter.createPlotTitleForWarnings(measureObjects, measureCodes);
        const plotLayout = this.getPlotLayoutObject(measureCodesWithNewAxisIndex, title);
        this.setState({ plotData, plotLayout });
      });
  }

  getBloodCellMeasuresAndConvertToPlotObjects() {
    const { measureCodes } = this.props;
    const bloodCellMeasureCodes = measureCodes.filter(measureCode => measureCode.class_field === 'immunology');
    Promise.all(bloodCellMeasureCodes
      .map(measureCode => this.getMeasure(measureCode.pk)))
      .then((bloodCellMeasureResponses) => {
        const bloodCellMeasures = bloodCellMeasureResponses.map(response => response.data);
        const sameMeasureIds = HealthGraph.getMeasuresWithSameMeasureIds(bloodCellMeasures);
        bloodCellMeasures.forEach((measureArray, index) => {
          bloodCellMeasures[index] = measureArray
            .filter(measure => sameMeasureIds.includes(measure.measure_id));
        });
        const plotData = MeasureConverter.convertBloodCellObjectsToPlotDataObjects(
          bloodCellMeasures, bloodCellMeasureCodes,
        );
        const plotLayout = MeasureConverter.createBloodCellPlotLayoutObject();
        this.setState({ plotData, plotLayout });
      });
  }

  getMeasuresForCorrelationPlot(measureCodes) {
    Promise.all(measureCodes
      .map(measureCode => this.getMeasure(measureCode.pk)))
      .then(([firstMeasure, secondMeasure]) => {
        const measureObjects = [firstMeasure, secondMeasure].map(response => response.data);
        measureObjects[0] = measureObjects[0].filter(measure => measureObjects[1]
          .map(measureObject => measureObject.date).includes(measure.date));
        measureObjects[1] = measureObjects[1].filter(measure => measureObjects[0]
          .map(measureObject => measureObject.date).includes(measure.date));
        const plotData = MeasureConverter.createCorrelationPlotDataObjects(measureObjects);
        const title = MeasureConverter.createPlotTitleForWarnings(measureObjects, measureCodes);
        const plotLayout = MeasureConverter.createCorrelationPlotLayoutObject(measureCodes, title);
        this.setState({ plotData, plotLayout });
      });
  }

  static getMeasuresWithSameMeasureIds(bloodCellMeasures) {
    const firstMeasureIds = bloodCellMeasures[0].map(measure => measure.measure_id);
    const otherMeasureIds = bloodCellMeasures.slice(1)
      .map(measureArray => measureArray.map(measure => measure.measure_id));
    return firstMeasureIds.filter(measureId => otherMeasureIds.filter(array => array
      .includes(measureId)).length === otherMeasureIds.length);
  }

  getPlotDataObjects(measureObjects, newAxisIndex) {
    return MeasureConverter
      .convertMeasureObjectsToPlotObjects(measureObjects, newAxisIndex);
  }

  getPlotLayoutObject(measureCodesWithNewAxisIndex, title) {
    return MeasureConverter.createPlotLayoutObject(measureCodesWithNewAxisIndex, title);
  }

  resetZoom() {
    const { plotLayout } = this.state;
    const noZoom = { range: undefined, autorange: true };
    plotLayout.yaxis = { ...plotLayout.yaxis, ...noZoom };
    plotLayout.xaxis = { ...plotLayout.xaxis, ...noZoom };
    if (plotLayout.yaxis2) { plotLayout.yaxis2 = { ...plotLayout.yaxis2, ...noZoom }; }
    this.setState({ plotLayout });
  }

  render() {
    const { plotLayout, plotData } = this.state;
    const {
      measureCodes,
      classes,
      logbookExpanded,
      measureSelectionExpanded,
      toggleMeasureSelectionExpanded,
      focusedDate,
      setFocusedDate,
    } = this.props;
    return (
      <div className={classes.fullHeight}>
        <MeasureSelection
          getMeasures={this.getMeasures}
          measureCodes={measureCodes}
          getMeasuresWithBloodPressure={this.getMeasuresWithBloodPressure}
          getBloodCellMeasures={this.getBloodCellMeasuresAndConvertToPlotObjects}
          getMeasuresForCorrelationPlot={this.getMeasuresForCorrelationPlot}
          measureSelectionExpanded={measureSelectionExpanded}
          toggleMeasureSelectionExpanded={toggleMeasureSelectionExpanded}
        />
        <div className={classes.centered}>
          <HealthGraphPlot
            plotData={plotData}
            plotLayout={plotLayout}
            resetZoom={this.resetZoom}
            logbookExpanded={logbookExpanded}
            measureSelectionExpanded={measureSelectionExpanded}
            focusedDate={focusedDate}
            setFocusedDate={setFocusedDate}
          />
        </div>
      </div>
    );
  }
}

HealthGraph.propTypes = {
  measureCodes: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  elephantId: PropTypes.number.isRequired,
  classes: PropTypes.shape({}).isRequired,
  logbookExpanded: PropTypes.bool.isRequired,
  measureSelectionExpanded: PropTypes.bool.isRequired,
  toggleMeasureSelectionExpanded: PropTypes.func.isRequired,
  focusedDate: PropTypes.shape(),
  setFocusedDate: PropTypes.func,
};

HealthGraph.defaultProps = {
  focusedDate: null,
  setFocusedDate: () => {},
};

export default withStyles(styles)(HealthGraph);



// WEBPACK FOOTER //
// ./src/components/health-graph/HealthGraph.jsx